<template>
    <Dialog v-model:open="opened">
        <DialogTrigger> <slot /> </DialogTrigger>
        <DialogContent>
            <DialogHeader class="items-center">
                <template #icon>
                    <ZoomLogo />
                </template>
                <DialogTitle>{{
                    $t("integrations.zoom.modal.title")
                }}</DialogTitle>
            </DialogHeader>

            <div class="px-6 py-4">
                <div
                    v-if="integration?.errors?.invalid_refresh_token"
                    class="gap-3 rounded-md border-red-200 bg-red-50 p-3 text-sm text-red-800 flex border"
                >
                    <AlertCircle :size="18" class="mt-0.5 shrink-0" />
                    <p>
                        {{ $t("integrations.errors.invalid_refresh_token") }}
                    </p>
                </div>
                <div
                    v-else
                    class="gap-3 rounded-md border-green-200 bg-green-50 p-3 text-sm text-green-800 flex border"
                >
                    <CheckCircle2 :size="18" class="mt-0.5 shrink-0" />
                    <p>
                        {{ $t("integrations.zoom.modal.active_description") }}
                    </p>
                </div>

                <div
                    class="mt-4 gap-3 pt-4 flex items-center justify-between border-t"
                >
                    <p class="text-sm text-muted-foreground">
                        {{ $t("integrations.zoom.verify.hint") }}
                    </p>
                    <Button
                        variant="outline"
                        size="sm"
                        :disabled="isVerifying"
                        @click="verifyConnection"
                    >
                        {{
                            isVerifying
                                ? $t("integrations.zoom.verify.checking")
                                : $t("integrations.zoom.verify.button")
                        }}
                    </Button>
                </div>

                <p class="mt-3 text-sm text-muted-foreground">
                    {{ $t("integrations.zoom.modal.remove_hint") }}
                </p>
            </div>

            <DialogFooter
                class="w-full"
                :class="cn(removable ? '!justify-between' : 'justify-end')"
            >
                <Button
                    v-if="removable"
                    @click="$emit('remove-integration')"
                    variant="destructive-ghost"
                    class="gap-1 flex"
                    size="xs"
                >
                    <Trash :size="16" />
                    {{ $t("integrations.remove_integration") }}
                </Button>

                <div class="flex-end flex">
                    <DialogClose>
                        <Button class="ml-4" variant="outline" type="button">{{
                            $t("cancel")
                        }}</Button>
                    </DialogClose>
                </div>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { cn } from "@/lib/utils";
import { ref } from "vue";
import { router } from "@inertiajs/vue3";
import axios from "axios";
import route from "ziggy-js";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogClose,
} from "@/components/ui/dialog-v2";
import ZoomLogo from "../logos/simple/Zoom.svg";
import { Trash, CheckCircle2, AlertCircle } from "lucide-vue-next";
import { Button } from "@/components/ui/button";
import { useI18n } from "vue-i18n";
import { useToast } from "@/components/ui/toast/use-toast";

const props = defineProps<{
    integration?: any;
    removable?: boolean;
}>();

const opened = ref(false);
const isVerifying = ref(false);
const { t } = useI18n();
const { toast } = useToast();

async function verifyConnection() {
    if (!props.integration?.id) {
        return;
    }

    isVerifying.value = true;
    try {
        const { data } = await axios.post(
            route("app.integrations.zoom.verify", {
                integration: props.integration.id,
            }),
        );

        if (data.status === "healthy") {
            toast({ title: t("integrations.zoom.verify.healthy") });
        } else if (data.status === "broken") {
            toast({
                title: t("integrations.zoom.verify.broken"),
                variant: "destructive",
            });
        } else {
            toast({
                title: t("integrations.zoom.verify.transient"),
                variant: "destructive",
            });
        }

        router.reload({ preserveScroll: true });
    } catch {
        toast({
            title: t("integrations.zoom.verify.transient"),
            variant: "destructive",
        });
    } finally {
        isVerifying.value = false;
    }
}
</script>

<style></style>
